import React, { Suspense, useState } from 'react'
import lazyWithRetry from '../../utils/lazyWithRetry'
import { useTranslation } from 'react-i18next'
import { useUser } from '../../auth'
import Overview from './contents/Overview'
import BannerSkeleton from './skeleton/BannerSkeleton'
import ButtonSkeleton from './skeleton/ButtonsSkeleton'
import ResultsSkeleton from './contents/skeleton/ResultsSkeleton'
import MyTaskWidgetSkeleton from './skeleton/MyTaskWidgetSkeleton'
import FeedSkeleton from './contents/skeleton/FeedSkeleton'
import { ANALYTICS_EVENTS } from '../../analytics/events'
import useTrackedSurface from '../../analytics/useTrackedSurface'

const WelcomeBanner = lazyWithRetry(() => import('./WelcomeBanner'), './WelcomeBanner')
const MyTask = lazyWithRetry(() => import('./MyTask'), './MyTask')
const ContentDashboard = lazyWithRetry(() => import('./contents/ContentDashboard'), './contents/ContentDashboard')
const Feed = lazyWithRetry(() => import('./contents/Feed'), './contents/Feed')

const TAB_EVENTS = {
    overview: ANALYTICS_EVENTS.DASHBOARD_OVERVIEW_VIEWED,
    contentResults: ANALYTICS_EVENTS.DASHBOARD_RESULTS_VIEWED,
    feed: ANALYTICS_EVENTS.DASHBOARD_FEED_VIEWED,
    myTask: ANALYTICS_EVENTS.DASHBOARD_TASKS_VIEWED,
}

/**
 * Tab bar + active panel. The tab itself is owned by Dashboard (URL state),
 * so the floating buttons there can hide on the very first paint of
 * `?tab=myTask` instead of one frame later.
 */
function Content({ activeTab, setActiveTab, contentList }) {
    const { t } = useTranslation()
    const { user } = useUser()
    const [refresh, setRefresh] = useState('')
    useTrackedSurface(TAB_EVENTS[activeTab])

    const contentListMap = {
        overview: t('overview'),
        contentResults: t('results'),
        feed: t('feedPosts'),
        myTask: t('myTasks'),
    }

    const renderContentMap = {
        overview: <Overview />,
        contentResults: (
            <Suspense fallback={<ResultsSkeleton />}>
                <ContentDashboard refresh={refresh} setRefresh={setRefresh} />
            </Suspense>
        ),
        feed: (
            <Suspense fallback={<FeedSkeleton />}>
                <Feed />
            </Suspense>
        ),
        myTask: (
            <Suspense fallback={<MyTaskWidgetSkeleton />}>
                <MyTask user={user} />
            </Suspense>
        ),
    }

    return (
        <div className="rounded-xl w-full flex flex-col h-full justify-center">
            <Suspense
                fallback={
                    <>
                        <BannerSkeleton />
                        <div className="flex flex-row gap-[15px] mt-[15px]">
                            <ButtonSkeleton count={contentList.length} />
                        </div>
                    </>
                }
            >
                <WelcomeBanner />

                <div className="flex flex-col bg-[#ECEBF8] z-[1] mt-[15px]">
                    <div className="flex flex-row gap-[15px]" role="tablist">
                        {contentList.map((button) => {
                            const isSelected = button === activeTab
                            return (
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={isSelected}
                                    id={`dashboard-button-${contentListMap[button]}`}
                                    key={button}
                                    className={`dsh-tab rounded-[10px] px-[30px] h-[60px] relative flex flex-row items-center justify-center gap-1 ${
                                        isSelected ? 'bg-highlightColor' : 'bg-[#5E639B] hover:!bg-highlightColor hover:!text-deepPurple'
                                    }`}
                                    onClick={() => setActiveTab(button)}
                                >
                                    <span className={`dashboard-welcome-button dsh-tab-label ${isSelected ? 'text-deepPurple' : 'text-highlightColor'}`}>
                                        {contentListMap[button]}
                                    </span>
                                    {isSelected && <span className="dsh-tab-arrow" aria-hidden="true" />}
                                </button>
                            )
                        })}
                    </div>
                </div>
            </Suspense>
            <div key={activeTab} className="dsh-panel-enter w-full mt-6 min-h-0 grow" role="tabpanel">
                {renderContentMap[activeTab]}
            </div>
        </div>
    )
}

export default Content
